"use client"

import { XMark } from "@medusajs/icons"
import { DialogTitle } from "@headlessui/react"
import Aside from "@modules/global-shell/components/aside"
import { useCartDrawer } from "../../context/cart-drawer-context"
import CartDrawerLine from "../cart-drawer-line"
import CartCampaignCountdown from "../cart-campaign-countdown"
import CartBundleIncentive from "../cart-bundle-incentive"
import CartDrawerSummary from "../cart-drawer-summary"
import CartEmpty from "../cart-empty"
import CartDrawerLoading from "../cart-drawer-loading"
import styles from "./cart-aside.module.css"
import type { CompanyLegalDetails } from "@modules/global-shell/components/company-legal-notice/format-company-legal-copy"

export default function CartAside({ legalDetails }: { legalDetails: CompanyLegalDetails }) {
  const {
    closeCart,
    isRefreshing,
    isAdding,
    pending,
    snapshot,
    model,
    error,
    noticeKind,
    noticeLineKey,
    refreshSnapshot,
    setLineQuantity,
  } = useCartDrawer()
  const hasLines = model.lines.length > 0
  const staleNotice = noticeKind === "stale" ? error : null
  const primaryPromo = !staleNotice && model.incentive ? model.incentive : null

  return (
    <Aside kind="cart" label="Sepet" labelledBy="cart-drawer-title" overlayTestId="cart-overlay">
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <DialogTitle id="cart-drawer-title" className={styles.title}>Sepet</DialogTitle>
          <span className={styles.itemCount}>{model.itemCount} ürün</span>
        </div>
        <button type="button" className={styles.closeButton} onClick={closeCart} aria-label="Sepeti kapat">
          <XMark aria-hidden="true" />
        </button>
      </header>

      {((isRefreshing && !snapshot.cart) || (isAdding && !hasLines)) ? (
        <main className={styles.cartMain} data-testid="cart-drawer-main" aria-busy="true">
          <CartDrawerLoading
            variant="body"
            label={isAdding ? "Ürün sepete ekleniyor…" : "Sepet yükleniyor…"}
          />
        </main>
      ) : snapshot.loadError ? (
        <main className={styles.cartMain} data-testid="cart-drawer-main" aria-busy="false">
          <CartEmpty state="error" error={snapshot.loadError} onRetry={refreshSnapshot} />
        </main>
      ) : (
        <main className={styles.cartMain} data-testid="cart-drawer-main">
          <CartEmpty hidden={hasLines} state="empty" onRetry={refreshSnapshot} onContinue={closeCart} />
          {hasLines ? (
            <div className={styles.cartDetails} data-testid="cart-details">
              {isAdding ? (
                <CartDrawerLoading variant="inline" label="Ürün sepete ekleniyor…" />
              ) : null}
              {(staleNotice || primaryPromo || model.campaign) ? (
                <div className={styles.commerceMessages} data-testid="cart-commerce-messages">
                  {staleNotice ? <p className={styles.staleNotice} role="status">{staleNotice}</p> : null}
                  {primaryPromo ? <CartBundleIncentive incentive={primaryPromo} /> : null}
                  {!staleNotice && model.campaign ? <CartCampaignCountdown campaign={model.campaign} /> : null}
                </div>
              ) : null}

              <div className={styles.cartLines}>
                <ul aria-label="Sepet ürünleri">
                  {model.lines.map((line) => (
                    <CartDrawerLine
                      key={line.key}
                      line={line}
                      currencyCode={model.currencyCode}
                      pending={pending}
                      locked={isAdding}
                      mutationError={noticeKind === "mutation-error" && noticeLineKey === line.key ? error : null}
                      setLineQuantity={setLineQuantity}
                      closeCart={closeCart}
                    />
                  ))}
                </ul>
              </div>

              <CartDrawerSummary
                total={model.total}
                savingsTotal={model.savingsTotal}
                currencyCode={model.currencyCode}
                pending={Boolean(pending) || isAdding}
                legalDetails={legalDetails}
              />
            </div>
          ) : null}
        </main>
      )}
    </Aside>
  )
}
